// src/data/carrierDefault.js
//
// Record 599 (Bob, 2026-10-07; task list section 597): THE 'DEFAULT CLAIM' WATCH. Built on the filed-claim
// screen as an automatic watch - a banner, and a letter the officer adopts and sends.
//
// THE QUESTION THIS ANSWERS, AND ONLY THIS ONE: the claim was presented on the carrier on a recorded date; the
// controlling agreement gives the carrier N days to notify a disallowance in writing and says what follows if it
// does not; N days have passed; is any written answer recorded on this platform? If not, the agreement's own
// words apply and the screen says so.
//
// WHERE EACH FACT COMES FROM (one source each, SS XI):
//   presentment   claim.presentedOn - ruling 70's record (src\data\claimPresentment.js). The `presented` act
//                 the ladder runs on is PROJECTED from the same field (offProperty.projectedPresentedAct), so a
//                 projected act is read only when the field itself is absent.
//   the answer    the claim's appealActs (src\data\appealLadder.js): carrier_answered / conference_decided with
//                 a real outcome, or an `appealed` act (an appeal answers a disallowance, so one was received).
//   extension     an `extended` act with granted !== false (ruling 267) replaces the due date.
//   the limit     agreementProfile.carrierDefault (src\data\claimRequirements.js): days, rule, the agreement's
//                 words VERBATIM, and the file:lines they were read from. No profile -> nothing is shown.
//
// WHAT IT DELIBERATELY DOES NOT DO:
//   - It never supplies a day-count. No carrierDefault on the profile -> state 'none'; the banner shows nothing.
//   - It does not decide that a late denial cures nothing. ANY recorded carrier answer, appeal or claim outcome
//     stops the watch (state 'none', reason given). Whether a denial served after the limit saves the carrier is
//     an officer's argument, not this module's verdict.
//   - It sends nothing. The letter below is a DRAFT the officer reads, adopts and sends himself.
//
// Pure: no React, no Firestore, no clock of its own - the caller passes nowMs.

import { letterHeader, resolveContactBlocks, NOT_ON_FILE, PARENT_ORGANIZATION } from './letterGroup';
import { lapsedAnswerWording } from './appealLadder';   // Record 612 (decision 28): one wording for a lapsed answer
import { lastDayOf } from './timeLimit';   // ruling 314: day one is the day of the event

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function str(v) { return typeof v === 'string' ? v.trim() : ''; }
function isDate(s) { return typeof s === 'string' && DATE_RE.test(s) && !Number.isNaN(Date.parse(s + 'T00:00:00Z')); }
function day(s) { return Date.parse(s + 'T00:00:00Z'); }
// The calendar date of nowMs where the reader is - the same local-day reading claimPresentment.isFutureISO uses.
function localDate(nowMs) {
  const d = new Date(typeof nowMs === 'number' && Number.isFinite(nowMs) ? nowMs : Date.now());
  const p = (n) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}

// An answer outcome that is an ANSWER. 'no_answer_on_record' is the ladder's own word for its absence.
const ANSWERED_OUTCOMES = ['denied', 'allowed', 'allowed_in_part'];

// The profile a claim's agreement carries, or null. Validated so a half-written entry shows nothing rather
// than a wrong date.
export function defaultProfileOf(agreementProfile) {
  const p = agreementProfile && agreementProfile.carrierDefault;
  if (!p) return null;
  if (!Number.isInteger(p.days) || p.days <= 0) return null;
  if (!str(p.rule) || !str(p.quote) || !str(p.source)) return null;
  return p;
}

const NONE = (reason, extra) => ({ state: 'none', due: null, rule: null, quote: null, source: null, reason, ...(extra || {}) });

// defaultStatus(claim, acts, agreementProfile, nowMs)
//   -> { state: 'none'|'running'|'expired', due: 'YYYY-MM-DD'|null, rule, quote, source, days, presentedOn,
//        extendedOn, reason }
export function defaultStatus(claim, acts, agreementProfile, nowMs) {
  const c = claim || {};
  const list = Array.isArray(acts) ? acts.filter(a => a && typeof a === 'object') : [];
  const prof = defaultProfileOf(agreementProfile);
  if (!prof) return NONE('no_profile');
  if (c.status === 'draft') return NONE('draft');
  if (str(c.outcome)) return NONE('closed');

  let presentedOn = str(c.presentedOn);
  if (!presentedOn) {
    const p = list.filter(a => a.kind === 'presented' && isDate(a.on)).sort((a, b) => day(a.on) - day(b.on)).pop();
    presentedOn = p ? p.on : '';
  }
  if (!isDate(presentedOn)) return NONE('not_presented');

  const answered = list.find(a => (a.kind === 'carrier_answered' || a.kind === 'conference_decided')
    && ANSWERED_OUTCOMES.includes(str(a.outcome)));
  if (answered) return NONE(str(answered.outcome) === 'denied' ? 'denial_recorded' : 'answer_recorded', { answeredOn: answered.on || null });
  if (list.some(a => a.kind === 'appealed')) return NONE('appealed');

  let due = lastDayOf(presentedOn, prof.days);   // ruling 314: day one is the day presented
  let extendedOn = null;
  const ext = list.filter(a => a.kind === 'extended' && a.granted !== false && isDate(a.on) && isDate(a.newDue)
    && day(a.on) >= day(presentedOn)).sort((a, b) => day(a.on) - day(b.on)).pop();
  if (ext) { due = ext.newDue; extendedOn = ext.on; }

  const today = localDate(nowMs);
  return {
    state: day(today) > day(due) ? 'expired' : 'running',
    due,
    rule: prof.rule,
    quote: prof.quote,
    source: prof.source,
    days: prof.days,
    from: prof.from || null,
    note: prof.note || null,
    presentedOn,
    extendedOn,
    reason: null,
  };
}

// The words the screen shows. Bob's wording (2026-10-07), filled from the status; '' when nothing is shown.
// Record 612 (Bob 10/8, decision 28 "agreed"): ONE WORDING for a lapsed carrier answer. The expired banner is
// appealLadder.lapsedAnswerWording - the sentence the off-property panel's silence watch prints - with the agreement's
// words quoted verbatim. "The claim is payable as presented" is retired; the expiry date is shown by the banner
// beside the wording (CarrierDefaultBanner), not inside it.
export function bannerText(status) {
  const s = status || {};
  if (s.state === 'expired') {
    return lapsedAnswerWording(s.rule, s.quote);
  }
  if (s.state === 'running') return 'Carrier answer due by ' + s.due + ' (' + s.rule + ')';
  return '';
}

// ---------------------------------------------------------------------------------------------
// THE DEFAULT LETTER - offPropertyLetters.js's pattern: the three-block header with the parent International at
// its head (ruling 188, PARENT_ORGANIZATION), a DRAFT stamp head and foot, only what is on the record, an
// absent fact printed [NOT ON FILE] and holding `sendable` false. The agreement's words are quoted verbatim
// with their file:lines (PD-16). Nothing here sends anything.
const DRAFT_HEAD =
  'DRAFT - FOR THE OFFICER TO REVIEW AND ADOPT. Not sent by the platform. It states only what is on\n'
+ 'the record; read it against the claim before it goes to the carrier, and sign and send it yourself.';
const FOOT =
  '\n\nEvery statement above is taken from the claim record and the agreement text as filed on this\n'
+ 'platform; the agreement is quoted verbatim from the file and lines cited. This is a DRAFT. The\n'
+ 'platform did not send it.';

export function defaultLetter(claim, status, ctx) {
  const c = claim || {};
  const s = status || {};
  if (s.state !== 'expired') {
    return { ok: false, because: 'A default letter is drafted only when the carrier time limit has expired with no written answer recorded.', text: '' };
  }
  const missing = [];
  const blocks = (ctx && ctx.blocks) ? ctx.blocks : resolveContactBlocks(c, ctx || {});
  const hdr = letterHeader(blocks, ctx && ctx.letterhead);   // Record 603: the committee letterhead (ruling 229), when loaded
  const number = str(c.id) || (missing.push('claim number'), NOT_ON_FILE);
  const claimant = str(c.claimantName) || (missing.push('claimant'), NOT_ON_FILE);
  const presentedTo = str(c.presentedTo) || (missing.push('the carrier officer the claim was presented to'), NOT_ON_FILE);
  const amount = c.amountClaimed !== undefined && c.amountClaimed !== null && String(c.amountClaimed).trim() !== ''
    ? String(c.amountClaimed).trim() : (missing.push('amount claimed'), NOT_ON_FILE);
  const unresolvedBlocks = ['generalCommittee', 'localCommittee', 'railroad'].filter(k => blocks[k] && !blocks[k].resolved).map(k => blocks[k].kind);

  const parts = [
    DRAFT_HEAD, '', hdr, '',
    'TO: ' + presentedTo,
    'RE: CLAIM ' + number + ' (' + claimant + ') - CARRIER TIME LIMIT EXPIRED; PAYMENT AS PRESENTED', '',
    'Claim ' + number + ' was presented on ' + s.presentedOn + (str(c.applicableAgreement) ? ' under the ' + str(c.applicableAgreement) : '') + '.',
    s.extendedOn
      ? 'The time for the Carrier to answer was extended by agreement on ' + s.extendedOn + ' to ' + s.due + '.'
      : 'The time for the Carrier to answer, ' + s.days + ' days from that date under ' + s.rule + ', ran to ' + s.due + '.',
    'No written notice of disallowance of this claim has been received.', '',
    s.rule + ' provides (' + s.source + '):',
    '"' + s.quote + '"', '',
    'The Carrier did not notify a disallowance within the time the agreement allows, and the consequence',
    s.rule + ' attaches to that, quoted above, applies. The Organization demands that claim ' + number + ' be paid as presented'
      + (amount !== NOT_ON_FILE ? ' (amount claimed: ' + amount + ')' : '') + '.', '',
    'Respectfully,', '', '_______________________________',
    FOOT,
  ];
  const uniq = Array.from(new Set(missing));
  return {
    ok: true, id: 'DEFAULT', title: 'Default letter - claim ' + number,
    text: parts.join('\n'), unresolvedBlocks, missingFields: uniq,
    sendable: unresolvedBlocks.length === 0 && uniq.length === 0,
  };
}

export { PARENT_ORGANIZATION };
